/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { Container, ContainerProps } from "reactstrap";
import StyledBox from "./StyledBox";
import { PageHeader } from "./PageHeader";

interface FullPageLayoutProps {
    children: React.ReactNode;
    title?: string;
    breadcrumbs?: React.ReactNode;
    withStyledBox?: boolean;
    fluidContainer?: boolean;
    pageHeader?: React.ReactNode;
}

/**
 * FullPageLayout component
 *
 * This component provides a flexible layout for full-page views, optionally including:
 * - A styled content box (`StyledBox`) if `withStyledBox` is enabled.
 * - A fluid or fixed-width container (`fluidContainer`).
 *
 * It is designed to wrap children components in a structured layout.
 * Mostly used as children in <DefaultLayout fullscreen=true/>
 */

const FullPageLayout = ({ withStyledBox, children, pageHeader, title, breadcrumbs, fluidContainer, className }: FullPageLayoutProps & ContainerProps) => {
    return (
        <div>
            {(pageHeader || title || breadcrumbs) && (
                <Container fluid={fluidContainer}>
                    <PageHeader breadcrumbs={breadcrumbs} title={title}>
                        {pageHeader}
                    </PageHeader>
                </Container>
            )}

            <Container fluid={fluidContainer} className={className} css={css`max-height: 100%; overflow-y: auto;`}>
                <div className={`pb-1 ${!pageHeader && !title && !breadcrumbs ? "pt-5" : "pt-0"}`}>
                    {withStyledBox
                        ? <StyledBox>{children}</StyledBox>
                        : children
                    }
                </div>
            </Container>
        </div>
    );
};

export default FullPageLayout;
